import React, { useState, useEffect, useMemo } from 'react';
import SEO from '../components/SEO';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PropertyCard from '../components/PropertyCard';
import { useTranslation } from '../contexts/LanguageContext';
import { useMarket } from '../contexts/MarketContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, SlidersHorizontal, X, LayoutGrid, List } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PropertiesPage() {
  const { t } = useTranslation();
  const { market, marketLink } = useMarket();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid'); // grid | list

  useEffect(() => {
    axios.get(`${API}/properties`, { params: { market } })
      .then(r => setProperties(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [market]);

  const filteredProperties = useMemo(() => {
    let result = (properties || []).filter(p => {
      const matchesSearch = !searchTerm ||
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
      const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    });

    // Sort
    if (sortBy === 'newest') result = [...result].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    else if (sortBy === 'oldest') result = [...result].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    else if (sortBy === 'name_asc') result = [...result].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    else if (sortBy === 'name_desc') result = [...result].sort((a, b) => (b.name || '').localeCompare(a.name || ''));

    return result;
  }, [properties, searchTerm, statusFilter, categoryFilter, sortBy]);

  const hasFilters = searchTerm || statusFilter !== 'all' || categoryFilter !== 'all';
  const clearFilters = () => { setSearchTerm(''); setStatusFilter('all'); setCategoryFilter('all'); };

  const statusCounts = useMemo(() => ({
    active: (properties || []).filter(p => p.status === 'active').length,
    upcoming: (properties || []).filter(p => p.status === 'upcoming').length,
    completed: (properties || []).filter(p => p.status === 'completed').length,
  }), [properties]);

  return (
    <div className="min-h-screen bg-background" data-testid="properties-page">
      <SEO title={t('nav.projects')} description={t('seo.properties_desc')} url="/properties" />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mb-4">{t('properties.title')}</h1>
          <p className="text-lg text-muted-foreground max-w-2xl">{t('properties.subtitle')}</p>
          {/* Status quick stats */}
          {!loading && (
            <div className="flex gap-4 mt-6 flex-wrap">
              <button onClick={() => setStatusFilter('active')}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all border ${statusFilter === 'active' ? 'bg-yellow-100 border-yellow-400 text-yellow-800' : 'bg-background border-border hover:border-yellow-400'}`}>
                <span className="w-2 h-2 rounded-full bg-yellow-500 inline-block"></span>
                {t('props.status.active')} ({statusCounts.active})
              </button>
              <button onClick={() => setStatusFilter('upcoming')}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all border ${statusFilter === 'upcoming' ? 'bg-blue-100 border-blue-400 text-blue-800' : 'bg-background border-border hover:border-blue-400'}`}>
                <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"></span>
                {t('props.status.upcoming')} ({statusCounts.upcoming})
              </button>
              <button onClick={() => setStatusFilter('completed')}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all border ${statusFilter === 'completed' ? 'bg-green-100 border-green-400 text-green-800' : 'bg-background border-border hover:border-green-400'}`}>
                <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                {t('props.status.completed')} ({statusCounts.completed})
              </button>
              {statusFilter !== 'all' && (
                <button onClick={() => setStatusFilter('all')}
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm text-muted-foreground hover:text-foreground border border-border">
                  <X className="w-3 h-3" /> {t('listings.all')}
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Sticky search bar */}
      <section className="py-4 border-b border-border sticky top-20 bg-background/95 backdrop-blur-sm z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t('properties.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-8"
                data-testid="search-input"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <SlidersHorizontal className="w-4 h-4 text-muted-foreground" />
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-36" data-testid="category-filter"><SelectValue placeholder={t('properties.category')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('properties.all_types')}</SelectItem>
                  <SelectItem value="residential">{t('properties.type_residential')}</SelectItem>
                  <SelectItem value="commercial">{t('properties.type_commercial')}</SelectItem>
                  <SelectItem value="hotel">{t('properties.type_hotel')}</SelectItem>
                  <SelectItem value="other">{t('properties.type_other')}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{t('properties.sort_newest')}</SelectItem>
                  <SelectItem value="oldest">{t('properties.sort_oldest')}</SelectItem>
                  <SelectItem value="name_asc">А → Я</SelectItem>
                  <SelectItem value="name_desc">Я → А</SelectItem>
                </SelectContent>
              </Select>
              {/* View toggle */}
              <div className="flex items-center border border-border rounded-lg overflow-hidden">
                <button onClick={() => setViewMode('grid')}
                  className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'}`}>
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button onClick={() => setViewMode('list')}
                  className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'}`}>
                  <List className="w-4 h-4" />
                </button>
              </div>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground gap-1">
                  <X className="w-3.5 h-3.5" /> Изчисти
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {error ? (
            <div className="text-center py-20">
              <p className="text-lg font-medium text-foreground mb-2">{t('properties.load_error')}</p>
              <p className="text-muted-foreground mb-6">{t('properties.load_error_hint')}</p>
              <Button variant="outline" onClick={() => window.location.reload()}>{t('properties.refresh')}</Button>
            </div>
          ) : loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="rounded-xl border border-border bg-secondary/30 animate-pulse h-64" />
              ))}
            </div>
          ) : filteredProperties.length === 0 ? (
            <div className="text-center py-20">
              <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium text-foreground mb-2">{t('properties.no_results')}</p>
              <p className="text-muted-foreground mb-6">{t('properties.no_results_hint')}</p>
              {hasFilters && (
                <Button variant="outline" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-2" /> Изчисти филтрите
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-muted-foreground">
                  {t('properties.showing')} <span className="font-medium text-foreground">{filteredProperties.length}</span> {t('properties.of')} {properties.length} {t('properties.projects_word')}
                  {hasFilters && <span className="text-primary ml-1">• {t('properties.filtered')}</span>}
                </p>
              </div>
              <div className={viewMode === 'grid'
                ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                : "flex flex-col gap-4"}>
                {filteredProperties.map((property) => (
                  <PropertyCard key={property.id} property={property} viewMode={viewMode} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
